import {
  ContentNode,
  ContentNodeUtils,
  EnumContentNode,
  EnumValueContentNode,
  InputObjectContentNode,
  InputObjectDataTypeConfig,
  InputValueContentNode,
  InputValueDataTypeConfig,
  ListContentNode,
} from '@helo/cms'
import { pipe } from '@helo/utils'
import assert from 'assert'

/**
 * Local type for sort editor representation
 * contentNode is an InputObjectContentNode with a single inputField
 * The inputField's ofType should be OrderByEnum (EnumContentNode)
 */
export type SortItem = {
  propertyKey: string
  contentNode: OrderByInputObjectContentNode
}

export type SortedSortItem = SortItem & {
  sortOrder: number
  id: string
}

export type OrderByEnumContentNode = EnumContentNode<
  'OrderByEnum',
  EnumValueContentNode<'ASC' | 'DESC'>
>
export type OrderByInputObjectContentNode = InputObjectContentNode<
  string,
  InputValueContentNode<string, OrderByEnumContentNode>
>

export type OrderByInputObjectDataTypeConfig = InputObjectDataTypeConfig<
  string,
  InputValueDataTypeConfig<string, OrderByEnumContentNode>
>

/**
 * Extracts the property key from an InputObjectContentNode by finding the inputField with a value
 */
const getPropertyKeyFromContentNode = (
  contentNode: InputObjectContentNode,
): string | null => {
  // Find the inputField that has a value (only one should have a value for sort items)
  const inputFieldWithValue = contentNode.fields.find(field => {
    if (!('key' in field)) return false
    const inputValueField = field as InputValueContentNode
    // Check if the ofType is an ENUM and has a contentNode (selected enum value)
    if (
      inputValueField.ofType.kind === 'ENUM' &&
      (inputValueField.ofType as EnumContentNode).contentNode
    ) {
      return true
    }
    return false
  }) as InputValueContentNode | undefined

  if (!inputFieldWithValue || !('key' in inputFieldWithValue)) {
    return null
  }

  return inputFieldWithValue.key
}

/**
 * Converts a ListContentNode (orderBy) to SortItem array
 * Each item in the list is an INPUT_OBJECT with data like { fieldName: 'ASC' } or { fieldName: 'DESC' }
 */
export const contentNodeToSortItems = (
  orderByNode: ListContentNode | null,
): SortItem[] => {
  if (!orderByNode || !orderByNode.items || orderByNode.items.length === 0) {
    return []
  }

  return orderByNode.items
    .map(item => {
      if (item.kind !== 'INPUT_OBJECT') {
        return null
      }

      const propertyKey = getPropertyKeyFromContentNode(item)

      if (!propertyKey) {
        return null
      }

      return {
        propertyKey,
        contentNode: item,
      } as SortItem
    })
    .filter((item): item is SortItem => item !== null)
}

/**
 * Gets the direction from a sort item's contentNode by traversing to the enum value
 */
const getDirectionFromSortItem = (item: SortItem): 'ASC' | 'DESC' => {
  const { propertyKey, contentNode } = item

  // Find the inputField that matches the propertyKey
  const inputField = contentNode.fields.find(
    field => 'key' in field && field.key === propertyKey,
  ) as InputValueContentNode | undefined

  if (!inputField) return 'ASC'

  // Get the ofType which should be an EnumContentNode
  const enumContentNode = inputField.ofType
  if (enumContentNode.kind !== 'ENUM') return 'ASC'

  // Get the selected enum value from the EnumContentNode's contentNode
  const enumValueContentNode = (enumContentNode as EnumContentNode)
    .contentNode as EnumValueContentNode | undefined

  if (!enumValueContentNode) return 'ASC'

  // Get the data from the enum value (should be 'ASC' or 'DESC')
  const direction = enumValueContentNode.data as 'ASC' | 'DESC' | null
  return direction === 'DESC' ? 'DESC' : 'ASC'
}

/**
 * Converts SortItem array to data format for orderBy ContentNode
 * Returns an array of objects like [{ fieldName: 'ASC' }, { anotherField: 'DESC' }]
 */
export const sortItemsToOrderByData = (
  sortItems: SortItem[],
): Array<Record<string, 'ASC' | 'DESC'>> => {
  return sortItems.map(item => {
    const { propertyKey } = item
    const direction = getDirectionFromSortItem(item)
    const result: Record<string, 'ASC' | 'DESC'> = {}
    result[propertyKey] = direction
    return result
  })
}

/**
 * Helper to get direction from contentNode by traversing to the enum value
 */
export const getDirectionFromContentNode = (
  contentNode: OrderByInputObjectContentNode,
  propertyKey: string,
): 'ASC' | 'DESC' => {
  return pipe(
    contentNode,
    ContentNodeUtils.findContentNode([
      ['key', propertyKey],
      ['kind', 'ENUM_VALUE'],
    ]),
    enumValueContentNode => {
      if (!enumValueContentNode)
        throw new Error(`Enum value not found for propertyKey: ${propertyKey}`)
      return enumValueContentNode as OrderByEnumContentNode
    },
    enumValueContentNode => enumValueContentNode.data ?? 'ASC',
  )
}

/**
 * Helper to create a new contentNode with propertyKey and direction
 */
export const createSortContentNode = (
  orderByInputObjectConfig: OrderByInputObjectDataTypeConfig,
  propertyKey: string,
  direction: 'ASC' | 'DESC',
): OrderByInputObjectContentNode => {
  // Create an empty InputObjectContentNode to start fresh
  const sortInputObject = ContentNodeUtils.createContentNodeWithSelection(
    orderByInputObjectConfig,
    {
      [propertyKey]: direction,
    },
  ) as InputObjectContentNode

  return sortInputObject as OrderByInputObjectContentNode
}

const validateOrderByInput = (
  orderByInput: ContentNode,
): orderByInput is InputValueContentNode => {
  assert(
    orderByInput.kind === 'INPUT_VALUE',
    'orderByInput must be an INPUT_VALUE',
  )
  assert(
    orderByInput.ofType.kind === 'LIST',
    'orderByInput.ofType must be an LIST',
  )
  assert(
    orderByInput.ofType.ofType.kind === 'INPUT_OBJECT',
    'orderByInput.ofType.ofType must be an INPUT_OBJECT',
  )
  assert(
    orderByInput.ofType.ofType.typeName.includes('OrderByInput'),
    'orderByInput.ofType.ofType.typeName must include "OrderByInput"',
  )

  return true
}

export const OrderByInputUtils = {
  validateOrderByInput,
  getSortItems: (
    orderByInput: InputValueContentNode,
  ): InputObjectContentNode[] => {
    const items = (orderByInput.ofType as ListContentNode)
      .items as InputObjectContentNode[]
    return items
  },
}
